import {
  VOICE_RECORDING_FAILURE_REASONS,
  VOICE_RECORDING_STATUSES,
  type VoiceRecordingFailure,
  type VoiceRecordingFailureReason,
  type VoiceRecordingStatus,
} from '@/shared-responder-v2-core';

/**
 * Opus in a WebM container first: it is what every browser that records audio
 * at all can produce, and it uploads at roughly a tenth of the WAV the legacy
 * responder built by hand from raw PCM. The MP4 fallback exists for Safari
 * builds that refuse WebM outright.
 */
export const VOICE_MIME_CANDIDATES: readonly string[] = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
];

const PERMISSION_ERROR_NAMES: ReadonlySet<string> = new Set([
  'NotAllowedError',
  'SecurityError',
]);

const DEVICE_ERROR_NAMES: ReadonlySet<string> = new Set([
  'AbortError',
  'NotFoundError',
  'NotReadableError',
  'OverconstrainedError',
]);

const FAILURE_STATUSES: Readonly<
  Record<VoiceRecordingFailureReason, VoiceRecordingStatus>
> = {
  [VOICE_RECORDING_FAILURE_REASONS.PermissionDenied]:
    VOICE_RECORDING_STATUSES.Denied,
  [VOICE_RECORDING_FAILURE_REASONS.Unsupported]:
    VOICE_RECORDING_STATUSES.Unsupported,
  [VOICE_RECORDING_FAILURE_REASONS.DeviceLost]: VOICE_RECORDING_STATUSES.Failed,
  [VOICE_RECORDING_FAILURE_REASONS.UploadFailed]:
    VOICE_RECORDING_STATUSES.Listening,
};

export interface TimedVoiceChunk {
  /** Milliseconds since the recorder started, at the moment the chunk closed. */
  readonly endedAtMs: number;
}

/**
 * Long enough that a whole answer is a handful of chunks, short enough that a
 * cut lands within a second of where the answer actually started.
 */
export const VOICE_CHUNK_INTERVAL_MS = 1000;

/** Ceiling on waiting for `requestData` in a browser that never answers it. */
export const CHUNK_FLUSH_TIMEOUT_MS = 500;

export const UNSUPPORTED_VOICE_FAILURE: VoiceRecordingFailure = {
  reason: VOICE_RECORDING_FAILURE_REASONS.Unsupported,
  message: null,
  isRetryable: false,
};

export type SignedUploadRequest = (
  fileName: string,
  mimeType: string
) => Promise<{ readonly url: string; readonly path: string } | null>;

export function supportsAudioRecording(): boolean {
  if (typeof window === 'undefined') return false;

  return (
    typeof navigator.mediaDevices?.getUserMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

export function toAudioConstraints(deviceId?: string): MediaStreamConstraints {
  return {
    video: false,
    audio: {
      autoGainControl: true,
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      ...(deviceId === undefined ? {} : { deviceId: { exact: deviceId } }),
    },
  };
}

export interface FlushableRecorder {
  readonly state: string;
  readonly requestData: () => void;
  readonly addEventListener: (
    type: 'dataavailable',
    listener: () => void,
    options: { readonly once: true }
  ) => void;
}

/**
 * `requestData` closes the chunk that is still open, which is what makes a cut
 * reach the last word spoken rather than the last full second. It is
 * asynchronous, so the cut waits for the resulting event — with a ceiling,
 * because a browser that silently ignores `requestData` must not strand the
 * answer.
 */
export function flushOpenChunk(
  recorder: FlushableRecorder | null
): Promise<void> {
  if (recorder === null || recorder.state !== 'recording') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const timeout = setTimeout(resolve, CHUNK_FLUSH_TIMEOUT_MS);

    recorder.addEventListener(
      'dataavailable',
      () => {
        clearTimeout(timeout);
        resolve();
      },
      { once: true }
    );

    recorder.requestData();
  });
}

function toErrorName(error: unknown): string {
  return error instanceof Error ? error.name : '';
}

function toErrorMessage(error: unknown): string | null {
  return error instanceof Error ? error.message : null;
}

export function pickVoiceMimeType(
  isSupported: (mimeType: string) => boolean
): string | null {
  return VOICE_MIME_CANDIDATES.find(isSupported) ?? null;
}

export function toVoiceRecordingFailure(error: unknown): VoiceRecordingFailure {
  const name = toErrorName(error);
  const message = toErrorMessage(error);

  if (PERMISSION_ERROR_NAMES.has(name)) {
    return {
      reason: VOICE_RECORDING_FAILURE_REASONS.PermissionDenied,
      message,
      isRetryable: false,
    };
  }

  if (DEVICE_ERROR_NAMES.has(name)) {
    return {
      reason: VOICE_RECORDING_FAILURE_REASONS.DeviceLost,
      message,
      isRetryable: true,
    };
  }

  return {
    reason: VOICE_RECORDING_FAILURE_REASONS.Unsupported,
    message,
    isRetryable: false,
  };
}

export function toUploadFailure(error: unknown): VoiceRecordingFailure {
  return {
    reason: VOICE_RECORDING_FAILURE_REASONS.UploadFailed,
    message: toErrorMessage(error),
    isRetryable: true,
  };
}

/**
 * A failed upload leaves the microphone exactly where it was — the answer it
 * could not carry is already typed or spoken, and killing capture would take
 * the next question's audio down with it.
 */
export function toVoiceRecordingStatus(
  failure: VoiceRecordingFailure
): VoiceRecordingStatus {
  return FAILURE_STATUSES[failure.reason];
}

/**
 * The chunks that cover the answer, plus the first one unconditionally.
 *
 * A `MediaRecorder` writes its container header into the opening chunk only, so
 * a slice taken from the middle of a continuous recording decodes as nothing at
 * all without it. Re-attaching the header is what makes one recorder able to
 * serve every question in the session instead of being torn down and rebuilt
 * per answer, which is where the legacy responder lost the first word.
 */
export function sliceVoiceChunks<TChunk extends TimedVoiceChunk>(
  chunks: readonly TChunk[],
  sinceMs: number
): readonly TChunk[] {
  const [header] = chunks;

  if (header === undefined) {
    return [];
  }

  const covering = chunks.filter((chunk) => chunk.endedAtMs > sinceMs);
  const [first] = covering;

  if (first === undefined) {
    return [];
  }

  return first === header ? covering : [header, ...covering];
}

export function buildVoiceAssetName(mimeType: string, nowMs: number): string {
  return mimeType.startsWith('audio/mp4')
    ? `voice-response-${nowMs}.mp4`
    : `voice-response-${nowMs}.webm`;
}
